/**
 * Verzögertes Absetzen von Events aus dem dl-event Attribut.
 *
 * Trägt ein Element neben dl-event auch success-signal-required, wird das Event nicht sofort
 * gesendet: Der Payload wird zum Auslösezeitpunkt eingesammelt und erst gesendet, wenn
 * dl-signal="success" gesetzt wird — am auslösenden Element oder an einem seiner Vorfahren zum
 * Klickzeitpunkt. Bei dl-signal="error" oder ausbleibendem Signal (Timeout) wird das Event
 * verworfen.
 *
 * @example
 * <my-component component="merkliste">
 *     <button dl-event="add_to_wishlist" success-signal-required product-id="4711">Merken</button>
 * </my-component>
 * // Komponente ersetzt ihren Inhalt und signalisiert an sich selbst:
 * this.innerHTML = html;
 * this.setAttribute("dl-signal", "success");
 *
 * Varianten, Voraussetzungen und Grenzen: Storybook → DataLayer Doku / 02 APIs /
 * 03 Dl Event Attribute → "Verzögerte Events"
 * ({@link ../../../../../../stories/datalayer/02-apis/03-dl-event-attribute.mdx})
 */
import { pushDlEventPayload } from "./dl-event-push.js";

export const SUCCESS_SIGNAL_REQUIRED_ATTRIBUTE = "success-signal-required";
export const SIGNAL_ATTRIBUTE = "dl-signal";
export const TIMEOUT_ATTRIBUTE = "success-signal-timeout";

const SUCCESS_VALUE = "success";
const DEFAULT_TIMEOUT_MS = 3000;

const pending = [];

/**
 * Hält einen dl-event Payload zurück, bis das Signal gesetzt wird.
 *
 * @param {HTMLElement} element - Trägerelement des dl-event Attributs
 * @param {Object} payload - Payload aus buildDlEventPayload()
 * @returns {boolean} true — das Event wurde behandelt (auch wenn noch nichts gesendet wurde)
 */
export function deferDlEvent(element, payload) {
	const entry = {
		element,
		// Zum Klickzeitpunkt festgehalten: Nach einem DOM-Update ist das auslösende Element
		// womöglich abgekoppelt, dann trägt ein überlebender Wrapper das Signal.
		signalTargets: collectSignalTargets(element),
		payload,
		observer: new MutationObserver(handleSignalMutations),
		timeoutId: null
	};

	for (const target of entry.signalTargets) {
		entry.observer.observe(target, {
			attributes: true,
			attributeFilter: [SIGNAL_ATTRIBUTE]
		});
	}

	entry.timeoutId = setTimeout(() => discardOnTimeout(entry), readTimeout(element));

	pending.push(entry);

	return true;
}

/**
 * Das auslösende Element und seine Vorfahren — alle Elemente, an denen das Signal akzeptiert
 * wird.
 * @private
 */
function collectSignalTargets(element) {
	const targets = [];

	for (let current = element; current; current = current.parentElement) {
		targets.push(current);
	}

	return targets;
}

/**
 * @private
 */
function handleSignalMutations(records) {
	for (const record of records) {
		resolveSignal(record.target);
	}
}

/**
 * Löst den ältesten passenden offenen Eintrag auf.
 *
 * Der Guard auf ein fehlendes Attribut ist zwingend: Das eigene removeAttribute() erzeugt
 * selbst wieder einen Mutation-Record, der sonst den nächsten offenen Eintrag fälschlich
 * verwerfen würde.
 *
 * Ein Signal löst genau einen Eintrag auf. Teilen sich mehrere offene Einträge einen Vorfahren,
 * feuern alle deren Observer — nur der erste Callback findet einen Eintrag und räumt das
 * Attribut ab, die übrigen laufen in den Guard.
 *
 * @private
 */
function resolveSignal(signalElement) {
	const signal = signalElement.getAttribute(SIGNAL_ATTRIBUTE);
	if (signal === null) {
		return;
	}

	const entry = findOldestEntry(signalElement);
	if (!entry) {
		return;
	}

	cleanup(entry);
	signalElement.removeAttribute(SIGNAL_ATTRIBUTE);

	if (signal === SUCCESS_VALUE) {
		pushDlEventPayload(entry.payload);
	}
}

/**
 * Ordnet ein Signal-Element einem offenen Eintrag über die zum Klickzeitpunkt festgehaltene
 * Element-Kette zu. Bei mehreren Treffern gewinnt der älteste Eintrag (FIFO) — relevant, wenn
 * mehrere offene Vorgänge sich einen Vorfahren teilen.
 *
 * @private
 */
function findOldestEntry(signalElement) {
	for (const entry of pending) {
		if (entry.signalTargets.includes(signalElement)) {
			return entry;
		}
	}

	return null;
}

/**
 * @private
 */
function discardOnTimeout(entry) {
	cleanup(entry);

	console.debug(
		`[data-layer] Kein ${SIGNAL_ATTRIBUTE} für "${entry.payload.eventName}" erhalten, Event wird nicht gesendet.`,
		entry.element
	);
}

/**
 * @private
 */
function cleanup(entry) {
	clearTimeout(entry.timeoutId);
	entry.observer.disconnect();

	const index = pending.indexOf(entry);
	if (index >= 0) {
		pending.splice(index, 1);
	}
}

/**
 * Liest das Timeout vom Element. Fehlende, nicht-numerische und nicht-positive Werte fallen
 * auf den Default zurück.
 * @private
 */
function readTimeout(element) {
	const configured = Number.parseInt(element.getAttribute(TIMEOUT_ATTRIBUTE), 10);

	if (Number.isNaN(configured) || configured <= 0) {
		return DEFAULT_TIMEOUT_MS;
	}

	return configured;
}
